import { useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import { getIsLoggingOut } from 'contexts/FirebaseContext';
import { rememberReturnPath } from 'shared/navigation/returnPath';

// ==============================|| AUTH GUARD ||============================== //

// hooks/useAuth is still a legacy .js module with no types of its own; this is the slice of
// it this guard consumes, declared at the seam instead of spreading `any` through the file —
// same call as features/account/components/ProfilePage.tsx's ProfileAuth.
interface AuthGuardAuth {
  isLoggedIn: boolean;
  // True once a signed-in user went away (contexts/accountReducer.ts). Optional: tests mock this hook.
  sessionEnded?: boolean;
}

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Authentication guard for routes
 */
export default function AuthGuard({ children }: AuthGuardProps) {
  const { isLoggedIn, sessionEnded } = useAuth() as unknown as AuthGuardAuth;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoggedIn) {
      // NEX-1585: the router state below is lost when the sign-in leaves the tab (the email link) or
      // reloads the page (a mobile social login), so the page — path, query and hash — is also kept
      // in storage for the auth screens to read back. A session that ended lands here too, and that
      // page is not somewhere to come back to: not when this tab logged out (getIsLoggingOut), and
      // not when another tab did or Firebase dropped the session (sessionEnded). Neither the storage,
      // which every tab shares, nor the router state, which would send the next sign-in in this very
      // tab back to the previous user's page, gets it.
      const sessionOver = getIsLoggingOut() || sessionEnded;
      if (!sessionOver) rememberReturnPath(`${location.pathname}${location.search}${location.hash}`);
      navigate('/login', { replace: true, state: sessionOver ? undefined : { from: location } });
    }
  }, [isLoggedIn, sessionEnded, navigate, location]);

  if (!isLoggedIn) {
    return null;
  }

  return children;
}
